import { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Loader2, Gift, Eye, AlertCircle, CheckCircle, XCircle, Tv, ExternalLink, Clock, AlertTriangle, RefreshCw } from 'lucide-react';
import { adsApi } from '../../api';
import { useAppStore } from '../../store/useAppStore';
import { useConfigStore } from '../../store/useConfigStore';
import { useTelegram, showOnclickaRewardedPopup } from '../../hooks';
import { useCombinedAds } from '../../hooks/useCombinedAds';
import toast from 'react-hot-toast';

// Enhanced logger utility with backend reporting for Railway monitoring
const API_URL = import.meta.env.VITE_API_URL || '/api';

const logger = {
  _send: async (level: string, msg: string, data?: Record<string, unknown>) => {
    try {
      await fetch(`${API_URL}/logs/frontend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level,
          message: msg,
          data,
          component: 'WatchAds',
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        }),
      });
    } catch {
      // Silent fail
    }
  },
  info: (msg: string, data?: Record<string, unknown>) => {
    console.log(`[WatchAds] ${msg}`, data || '');
  },
  warn: (msg: string, data?: Record<string, unknown>) => {
    console.warn(`[WatchAds] ${msg}`, data || '');
    logger._send('warn', msg, data);
  },
  error: (msg: string, data?: Record<string, unknown>) => {
    console.error(`[WatchAds] ${msg}`, data || '');
    logger._send('error', msg, data);
  },
};

type AdState = 'idle' | 'loading' | 'showing' | 'showing-first' | 'showing-second' | 'verifying' | 'success' | 'error';

interface AdCooldown {
  adType: string;
  network: string;
  remainingSeconds: number;
  totalSeconds: number;
}

interface AdButton {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  reward: number;
  adType: 'watch_video' | 'quick_view' | 'sponsor_page';
  cooldownKey: string; // Key to match with cooldown data
}

export function WatchAds() {
  const [adState, setAdState] = useState<AdState>('idle');
  const [lastReward, setLastReward] = useState<number>(0);
  const [_selectedAd, setSelectedAd] = useState<AdButton | null>(null);
  const [cooldowns, setCooldowns] = useState<AdCooldown[]>([]);
  const [globalCooldown, setGlobalCooldown] = useState<number>(0);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const updateBalance = useAppStore((state) => state.updateBalance);
  const user = useAppStore((state) => state.user);
  const { hapticFeedback, hapticNotification } = useTelegram();
  
  // Get reward amounts from config store
  const adsgram = useConfigStore((state) => state.adsgram);
  const onclicka = useConfigStore((state) => state.onclicka);

  // Combined ads hooks for watch_video and quick_view
  const combinedAdsVideo = useCombinedAds({
    ymid: 'watch_ads_video',
    onStart: () => setAdState('loading'),
    onTransition: (_from, to) => {
      if (to === 'onclicka') setAdState('showing-second');
    },
  });

  const combinedAdsInterstitial = useCombinedAds({
    ymid: 'watch_ads_quick',
    onStart: () => setAdState('loading'),
    onTransition: (_from, to) => {
      if (to === 'onclicka') setAdState('showing-second');
    },
  });

  // Fetch cooldowns from backend
  const fetchCooldowns = useCallback(async () => {
    try {
      const response = await adsApi.getAdCooldowns();
      
      // Defensive: ensure cooldowns is an array
      const cooldownsData = Array.isArray(response?.cooldowns) ? response.cooldowns : [];
      const globalCooldownData = typeof response?.globalCooldown === 'number' ? response.globalCooldown : 0;
      
      setCooldowns(cooldownsData);
      setGlobalCooldown(globalCooldownData);
      setComponentError(null);
      setIsInitializing(false);
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { error?: string } }; message?: string };
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load ads';
      const statusCode = err.response?.status;
      
      logger.error('Failed to fetch cooldowns', {
        error: errorMessage,
        statusCode,
        userId: user?.telegramId,
      });
      
      // Only set error on initial load, not on periodic refreshes
      if (isInitializing) {
        if (statusCode === 403) {
          setComponentError('Your account has been restricted from viewing ads.');
        } else if (statusCode === 429) {
          setComponentError('Too many requests. Please wait a moment.');
        } else if (statusCode === 401) {
          setComponentError('Session expired. Please refresh the page.');
        } else {
          setComponentError(errorMessage);
        }
        setIsInitializing(false);
      }
    }
  }, [user?.telegramId, isInitializing]);

  // Fetch cooldowns on mount and after ad completion
  useEffect(() => {
    logger.info('WatchAds component mounted', { userId: user?.telegramId });
    fetchCooldowns();
    // Refresh cooldowns every 5 seconds
    const interval = setInterval(fetchCooldowns, 5000);
    return () => clearInterval(interval);
  }, [fetchCooldowns]);

  // Countdown timer for local UI updates
  useEffect(() => {
    // Defensive: ensure cooldowns is an array
    const cooldownsArr = Array.isArray(cooldowns) ? cooldowns : [];
    if (cooldownsArr.some(c => c.remainingSeconds > 0) || globalCooldown > 0) {
      const timer = setInterval(() => {
        setCooldowns(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          return prevArr.map(c => ({
            ...c,
            remainingSeconds: Math.max(0, c.remainingSeconds - 1)
          }));
        });
        setGlobalCooldown(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldowns, globalCooldown]);

  // Get cooldown for a specific ad type
  const getCooldownForAd = (adType: string): number => {
    // Check global cooldown first
    if (globalCooldown > 0) return globalCooldown;
    
    // Defensive: ensure cooldowns is an array before filtering
    if (!Array.isArray(cooldowns)) return 0;
    
    // Find matching cooldowns for this ad type
    const matchingCooldowns = cooldowns.filter(c => c.adType === adType);
    if (matchingCooldowns.length === 0) return 0;
    
    // Return the maximum remaining cooldown
    return Math.max(...matchingCooldowns.map(c => c.remainingSeconds));
  };

  // Format seconds to mm:ss
  const formatCooldown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  // Calculate reward for dual-ad buttons (same reward as before, just showing both ads)
  // Fallback values MUST match DEFAULT_CONFIG in backend/src/models/SystemConfig.ts
  const watchVideoReward = adsgram.rewardAmount ?? 20;
  const quickViewReward = adsgram.interstitialReward ?? 15;

  // Build streamlined ad buttons
  const AD_BUTTONS: AdButton[] = useMemo(() => [
    {
      id: 'watch_video',
      title: 'Watch Video',
      description: 'Watch video ads for rewards',
      icon: <Play className="w-5 h-5" />,
      color: 'from-orange-500 to-red-500',
      reward: watchVideoReward,
      adType: 'watch_video',
      cooldownKey: 'watch_video',
    },
    {
      id: 'quick_view',
      title: 'Quick View',
      description: 'Fast content view',
      icon: <Tv className="w-5 h-5" />,
      color: 'from-blue-500 to-purple-500',
      reward: quickViewReward,
      adType: 'quick_view',
      cooldownKey: 'quick_view',
    },
    {
      id: 'sponsor_page',
      title: 'Sponsor Page',
      description: 'Visit sponsor',
      icon: <ExternalLink className="w-5 h-5" />,
      color: 'from-cyan-500 to-blue-500',
      reward: onclicka.rewardAmount ?? 20,
      adType: 'sponsor_page',
      cooldownKey: 'sponsor_page',
    },
  ], [adsgram, onclicka, watchVideoReward, quickViewReward]);

  // Handle ad button click - now uses session tokens for fraud prevention
  const handleAdClick = async (adButton: AdButton) => {
    setSelectedAd(adButton);
    setAdState('loading');
    hapticFeedback('medium');

    let sessionToken: string | null = null;

    try {
      let success = false;
      let adNetwork = 'combined'; // Track as combined for dual-ad types

      // Step 1: Start ad session on server BEFORE showing ad
      try {
        const sessionResult = await adsApi.startAdSession(adButton.id, adButton.adType);
        if (!sessionResult.success || !sessionResult.sessionToken) {
          throw new Error('Failed to start ad session');
        }
        sessionToken = sessionResult.sessionToken;
      } catch (sessionError: any) {
        console.error('Failed to start ad session:', sessionError);
        toast.error(sessionError.response?.data?.error || 'Failed to start. Please try again.');
        setAdState('idle');
        setSelectedAd(null);
        return;
      }

      // Step 2: Show the actual ad
      switch (adButton.adType) {
        case 'watch_video': {
          // Show combined ads: AdsGram rewarded first, then Onclicka (same reward)
          setAdState('showing-first');
          const result = await combinedAdsVideo.showCombinedAds();
          success = result.success;
          // Determine network for backend tracking
          if (result.adsgramCompleted && result.onclickaCompleted) {
            adNetwork = 'combined';
          } else if (result.adsgramCompleted) {
            adNetwork = 'adsgram';
          } else if (result.onclickaCompleted) {
            adNetwork = 'onclicka';
          }
          break;
        }

        case 'quick_view': {
          // Show combined ads: AdsGram interstitial first, then Onclicka (same reward)
          setAdState('showing-first');
          const result = await combinedAdsInterstitial.showCombinedInterstitialAds();
          success = result.success;
          if (result.adsgramCompleted && result.onclickaCompleted) {
            adNetwork = 'combined';
          } else if (result.adsgramCompleted) {
            adNetwork = 'adsgram';
          } else if (result.onclickaCompleted) {
            adNetwork = 'onclicka';
          }
          break;
        }

        case 'sponsor_page':
          // Show Onclicka rewarded popup ad (single ad)
          setAdState('showing');
          success = await showOnclickaRewardedPopup('watch_ads');
          adNetwork = 'onclicka';
          break;
      }

      if (success && sessionToken) {
        setAdState('verifying');
        
        // Record ad view in backend with session token - SERVER determines the reward amount
        try {
          const result = await adsApi.recordAdView(adNetwork, adButton.adType, sessionToken);
          
          // Update balance from backend response
          if (result.newBalance !== undefined) {
            updateBalance(result.newBalance);
          }
          
          // Use server-calculated reward, fallback to expected amount for UI only
          const earnedReward = result.reward ?? adButton.reward;
          setLastReward(earnedReward);
          toast.success(`+${earnedReward} points earned!`);
        } catch (e: any) {
          console.error('Failed to record ad view:', e);
          // Show error but don't give fake reward
          if (e.response?.status === 429) {
            toast.error(e.response?.data?.error || 'Please wait before watching another ad');
            setAdState('error');
            setTimeout(() => {
              setAdState('idle');
              setSelectedAd(null);
            }, 2000);
            return;
          } else {
            toast.error('Failed to record reward. Please try again.');
            setAdState('error');
            setTimeout(() => {
              setAdState('idle');
              setSelectedAd(null);
            }, 2000);
            return;
          }
        }
        
        setAdState('success');
        hapticNotification('success');

        // Refresh cooldowns after successful ad
        fetchCooldowns();

        // Reset after showing success
        setTimeout(() => {
          setAdState('idle');
          setSelectedAd(null);
        }, 2000);
      } else {
        throw new Error('Ad failed to display');
      }
    } catch (error) {
      console.error('Ad error:', error);
      setAdState('error');
      hapticNotification('error');
      toast.error('Ad failed to load. Try again later.');

      setTimeout(() => {
        setAdState('idle');
        setSelectedAd(null);
      }, 2000);
    }
  };

  // Show loading state while initializing
  if (isInitializing) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="card p-6"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-orange-500" />
            <p className="text-white/60 text-sm">Loading ad options...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  // Show error state if component failed to load
  if (componentError) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="card p-6 bg-red-500/10 border border-red-500/30"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-red-400 mb-2">Watch Ads Error</h3>
            <p className="text-sm text-white/60 mb-4">{componentError}</p>
            <button
              onClick={() => {
                setComponentError(null);
                setIsInitializing(true);
                fetchCooldowns();
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 text-sm transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="card p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Play className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Watch Ads</h3>
            <p className="text-white/60 text-sm">
              Earn points by watching ads
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/5 rounded-xl p-4 text-center">
          <Eye className="w-5 h-5 mx-auto mb-2 text-blue-400" />
          <p className="text-white/60 text-xs mb-1">Ad Types</p>
          <p className="font-bold text-lg">{AD_BUTTONS.length}</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 text-center">
          <Gift className="w-5 h-5 mx-auto mb-2 text-green-400" />
          <p className="text-white/60 text-xs mb-1">Earn Per Ad</p>
          <p className="font-bold text-lg">{Math.min(watchVideoReward, quickViewReward, onclicka.rewardAmount ?? 20)}-{Math.max(watchVideoReward, quickViewReward, onclicka.rewardAmount ?? 20)}</p>
        </div>
      </div>

      {/* Ad State Display */}
      <AnimatePresence mode="wait">
        {adState === 'loading' && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="py-8 text-center"
          >
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-orange-500" />
            <p className="text-white/60">Preparing ad...</p>
          </motion.div>
        )}

        {adState === 'showing' && (
          <motion.div
            key="showing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="py-8 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/20 flex items-center justify-center">
              <Play className="w-8 h-8 text-orange-500 animate-pulse" />
            </div>
            <p className="text-white/80 font-medium mb-2">Ad is playing...</p>
            <p className="text-white/40 text-sm">Complete the ad to earn your reward</p>
          </motion.div>
        )}

        {adState === 'showing-first' && (
          <motion.div
            key="showing-first"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="py-8 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/20 flex items-center justify-center">
              <Play className="w-8 h-8 text-orange-500 animate-pulse" />
            </div>
            <p className="text-white/80 font-medium mb-2">Ad 1 of 2 playing...</p>
            <div className="flex justify-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <div className="w-3 h-3 rounded-full bg-white/20"></div>
            </div>
            <p className="text-white/40 text-sm">Complete both ads for full reward</p>
          </motion.div>
        )}

        {adState === 'showing-second' && (
          <motion.div
            key="showing-second"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="py-8 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Play className="w-8 h-8 text-purple-500 animate-pulse" />
            </div>
            <p className="text-white/80 font-medium mb-2">Ad 2 of 2 playing...</p>
            <div className="flex justify-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            </div>
            <p className="text-white/40 text-sm">Almost done! Complete for full reward</p>
          </motion.div>
        )}

        {adState === 'verifying' && (
          <motion.div
            key="verifying"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="py-8 text-center"
          >
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-500" />
            <p className="text-white/60">Verifying completion...</p>
          </motion.div>
        )}

        {adState === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="py-8 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-white/80 font-medium mb-2">Ad Completed!</p>
            <p className="text-2xl font-bold text-green-400">+{lastReward} points</p>
          </motion.div>
        )}

        {adState === 'error' && (
          <motion.div
            key="error"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="py-8 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-white/80 font-medium">Ad Failed</p>
            <p className="text-white/40 text-sm">Please try again</p>
          </motion.div>
        )}

        {adState === 'idle' && (
          <motion.div
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Ad Buttons */}
            <div className="space-y-3">
              {AD_BUTTONS.map((adButton) => {
                const cooldownSeconds = getCooldownForAd(adButton.cooldownKey);
                const isOnCooldown = cooldownSeconds > 0;
                
                return (
                  <motion.button
                    key={adButton.id}
                    whileTap={{ scale: isOnCooldown ? 1 : 0.98 }}
                    onClick={() => !isOnCooldown && handleAdClick(adButton)}
                    disabled={isOnCooldown}
                    className={`w-full p-4 rounded-xl flex items-center justify-between transition-all ${
                      isOnCooldown 
                        ? 'bg-white/5 opacity-60 cursor-not-allowed' 
                        : 'bg-white/5 hover:bg-white/10 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${adButton.color} ${isOnCooldown ? 'opacity-50' : ''}`}>
                        {isOnCooldown ? <Clock className="w-5 h-5" /> : adButton.icon}
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{adButton.title}</p>
                        <p className="text-white/40 text-sm">
                          {isOnCooldown 
                            ? <span className="text-yellow-400">Cooldown: {formatCooldown(cooldownSeconds)}</span>
                            : adButton.description}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {isOnCooldown ? (
                        <div className="flex items-center gap-1 text-yellow-400">
                          <Clock className="w-4 h-4" />
                          <span className="font-bold">{formatCooldown(cooldownSeconds)}</span>
                        </div>
                      ) : (
                        <>
                          <p className="font-bold text-green-400">+{adButton.reward}</p>
                          <p className="text-white/40 text-xs">points</p>
                        </>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info box */}
      <div className="mt-6 p-4 rounded-xl bg-white/5 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-white/60">
          <p className="mb-1">Tips for earning more:</p>
          <ul className="list-disc list-inside space-y-1 text-white/40">
            <li>Watch ads completely for full rewards</li>
            <li>Ads rotate between networks for variety</li>
            <li>Complete daily quests for bonus points</li>
          </ul>
        </div>
      </div>
    </motion.div>
  );
}
